import React, { useEffect, useRef, useState } from 'react'
import { Redirect } from 'react-router-dom'
import {
  getBooleanSessionStorage,
  setJSONSessionStorage,
} from '@ally/utilitarian'
import styled from 'styled-components'
import { Box, Space, TextHeading, useMediaSize } from '@ally/metronome-ui'
import { Domains } from '@ally/data-types-host'
import { CustomizationOptions } from '@ally/data-types-customization'
import { useHostServices } from '../HostServices'
import { useDefaultSubnav } from '../hooks'
import { pageViewAnalytics, trackCCOnDashboardLoad } from '../analytics'
import { useAccountTypes, useDebtPlanFocusAccount } from '../providers'
import { CustomizeDataObj, DashboardView } from '../CustomizeDashboard/types'
import {
  GhostTiles,
  Greeting,
  NonPrintingWrapper,
  TipsAndOpportunities,
  TotalBalance,
  WiresNotification,
} from '../components'
import CustomizeApiAlert from '../CustomizeDashboard/CustomizeApiAlert'
import CustomizeModal from '../CustomizeDashboard/CustomizeModal'
import ViewToggle from '../CustomizeDashboard/ViewToggle'
import { getWidgetContainer } from '../CustomizeDashboard/WidgetsContainer'
import { useCustomizeDashboard } from '../CustomizeDashboard/CustomizeDashboardContext/CustomizeDashboardContext'
import { HAS_VISITED_DASHBOARD_ERROR_KEY } from './DashboardError'
import { lobComponents } from '../CustomizeDashboard/constants'
import { getRoles } from '../utils'
import VehicleSoftLinkMessage from '../components/Vehicles/VehicleSoftLinkMessage'

const StyledContainer = styled(Box)<{ view: DashboardView }>`
  ${({ theme: { media, space }, view }): string => `
      margin-left: -${space.xs};
      margin-right: -${space.xs};
      display: flex;
      flex-direction: ${view === DashboardView.TILE ? 'row' : 'column'};

      ${media.down('md')} {
        margin-left: auto;
        margin-right: auto;
      }

      @media print {
        display: block;
        margin: 0;
      }
  `}
`
const LobContainer = styled(Box)``
const LeftColumn = styled(Box)`
  ${({ theme: { media, space } }): string => `
  ${media.up('md')} {
      margin-left: ${space.xs};
      margin-right: ${space.xs};
    }
    ${media.down('md')} {
      flex-basis: 100%;
    }
  `}
`
const WidgetColumn = styled(Box)`
  ${({ theme: { space } }): string => `
    display: flex;
    flex-direction: column;
    margin-top: 52px;
    gap: ${space.lg};
    padding-left: ${space.sm};
    padding-right: ${space.xs};

    > div {
      flex-grow: unset;
    }
  `}
`
const WidgetRow = styled(Box)`
  ${({ theme: { space } }): string => `
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
    gap: ${space.lg};
    margin-bottom: ${space.lg};
  `}
`
const CustomizationTriggers = styled(Box)`
  ${({ theme: { media, space } }): string => `
    margin-bottom: -27px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    /* create a new stacking context so that the customize buttons are above the first LOB header */
    isolation: isolate;

    ${media.down('sm')} {
      margin-bottom: ${space.xs};
    }
  `}
`

function getSortedLobComponents(
  customizeData: CustomizeDataObj | CustomizationOptions,
): React.ReactElement[] {
  return [...(customizeData?.lobs ?? [])]
    .sort((a, b) => a.order - b.order)
    .map(lob => lobComponents[lob.name])
}

const Dashboard: React.FC = () => {
  const isSmDown = useMediaSize('SmDown')
  const isMdDown = useMediaSize('MdDown')
  const isLgDown = useMediaSize('LgDown')
  const isXl = useMediaSize('XlOnly')

  const {
    hostData,
    session,
    analytics,
    featureFlags,
    actionBar,
  } = useHostServices()
  const roles = getRoles(session)
  useDefaultSubnav()
  const {
    activeDepositAccounts,
    allCCAccounts,
    hasAtLeastOneActiveAccount,
    hasSingleAccountType,
    isBankUser,
    isCCUser,
    isDebtPlanUser,
    isFullOutage,
    isInvestUser,
    isPartialOutage,
    isWealthUser,
  } = useAccountTypes()
  const [
    {
      dashboardView,
      customizeApiAlert,
      isCustomizationEnabled,
      eligibleWidgets,
      widgets,
      customizeApiData,
    },
  ] = useCustomizeDashboard()

  const isEligibleForCustomization =
    Object.keys(eligibleWidgets).length > 0 || !hasSingleAccountType

  const canToggleView =
    isCustomizationEnabled && !isLgDown && widgets.some(w => w.enabled)
  const canReorderAccounts = isCustomizationEnabled && !hasSingleAccountType
  const canManageWidgets =
    isCustomizationEnabled && Object.keys(eligibleWidgets).length > 0
  const sortedLobs = getSortedLobComponents(customizeApiData)

  // Setting local storage for analytics
  useEffect(() => {
    const hasVisitedDashboard = getBooleanSessionStorage('hasVisitedDashboard')
    if (!hasVisitedDashboard) {
      setJSONSessionStorage('hasVisitedDashboard', true)
      setJSONSessionStorage('isFirstDashboardVisit', true)
    }
    return (): void => window.sessionStorage.removeItem('isFirstDashboardVisit')
  }, [])

  const enabledWidgets = widgets.filter(w => w.enabled)

  useEffect(() => {
    pageViewAnalytics(
      analytics,
      hostData,
      session,
      isMdDown,
      enabledWidgets,
      dashboardView,
      activeDepositAccounts,
    )
    if (!isFullOutage && isCCUser) {
      trackCCOnDashboardLoad(allCCAccounts)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // TODO: Revisit this with Logan to fix the "don't lie about our dependencies" issue, it was firing multiple times as it was (should only fire once)

  const widgetHeight = 318
  const [widgetSlice, setWidgetSlice] = useState(1)
  const lobContainerRef = useRef<HTMLDivElement>(null)
  const showTotalBalance =
    !isPartialOutage && (isBankUser || isInvestUser || isWealthUser)
  const showColumnWidgets = dashboardView === DashboardView.TILE && isXl

  // mutableWidgets will be spliced and mutated to determine column and row
  const mutableWidgets = [...enabledWidgets]
  const columnWidgets = showColumnWidgets
    ? mutableWidgets.splice(0, widgetSlice)
    : []

  // this could be all of the remaining widgets not in the column if in TILE view
  const rowWidgets = mutableWidgets

  const debtPlanFocus = useDebtPlanFocusAccount()

  useEffect(() => {
    if (showColumnWidgets && lobContainerRef?.current?.clientHeight) {
      const slice = Math.floor(
        lobContainerRef.current.clientHeight / widgetHeight,
      )
      if (slice > 0) {
        setWidgetSlice(slice)
      }
    }
  }, [showColumnWidgets, isXl])

  const wiresNotificationFFValue = featureFlags.variation(
    'ff_wires-notification',
    'noMessage',
  )

  useEffect(() => {
    const showNotification = ['stageOne', 'stageTwo', 'stageThree']

    if (showNotification.includes(wiresNotificationFFValue)) {
      actionBar.notifications.set([
        {
          type: 'REMOTE',
          innerContent: (
            <WiresNotification
              wiresNotificationFFValue={wiresNotificationFFValue}
            />
          ),
        },
      ])
    }
  }, [wiresNotificationFFValue])

  if (session.status !== 'Authenticated') {
    return <Redirect to="/" />
  }

  // A final guard in case a Guest user is mistakenly redirected to /dashboard
  const isGuestEnabled = featureFlags.variation('FF_guest-experience', false)
  if (isGuestEnabled && roles.guest) {
    return <Redirect to="/guest-dashboard" />
  }

  if (isFullOutage) {
    if (sessionStorage.getItem(HAS_VISITED_DASHBOARD_ERROR_KEY)) {
      if (isDebtPlanUser) debtPlanFocus.update()
      // fetch accounts call again, rendering app skeleton loader
      hostData[Domains.AFG_ACCOUNTS].update()
      sessionStorage.removeItem(HAS_VISITED_DASHBOARD_ERROR_KEY)
    } else {
      return <Redirect to="/dashboard-error" />
    }
  }
  // checks for no-account users to redirect to no-accounts page
  if (!isFullOutage && !isPartialOutage && !hasAtLeastOneActiveAccount) {
    return <Redirect to="/no-accounts" />
  }

  return (
    <>
      <NonPrintingWrapper>
        <Box
          display="flex"
          justifyContent="space-between"
          data-testid="snapshot-container"
        >
          {/* flex=1 to prevent wrapping issue when name is longer than 20 characters - WEB-66363 */}
          <Box flex="1">
            <TextHeading tag="h1" size="lg" tabIndex={-1}>
              Snapshot
            </TextHeading>
            <Greeting />
          </Box>
          {showTotalBalance && <TotalBalance />}
        </Box>
        <TipsAndOpportunities />
        {isEligibleForCustomization && customizeApiAlert && (
          <Box mt="sm" mb={isSmDown ? undefined : 'xl'}>
            <CustomizeApiAlert
              shouldRender={
                customizeApiAlert.isDashboardError &&
                customizeApiAlert.shouldRender
              }
              type={customizeApiAlert.type}
              isDashboardError={customizeApiAlert.isDashboardError}
            />
          </Box>
        )}
        {canReorderAccounts || canManageWidgets ? (
          <CustomizationTriggers>
            {canToggleView && <ViewToggle />}
            <Space ml="sm">
              <CustomizeModal />
            </Space>
          </CustomizationTriggers>
        ) : null}
      </NonPrintingWrapper>
      <StyledContainer view={dashboardView}>
        <LeftColumn data-testid="snapshot-left-column">
          <LobContainer data-testid="lob-container" ref={lobContainerRef}>
            {sortedLobs}
            <VehicleSoftLinkMessage />
          </LobContainer>
          <WidgetRow data-testid="widget-container-row">
            {getWidgetContainer(isCustomizationEnabled, rowWidgets)}
          </WidgetRow>
        </LeftColumn>
        {showColumnWidgets && (
          <WidgetColumn data-testid="widget-container-column">
            {getWidgetContainer(isCustomizationEnabled, columnWidgets)}
          </WidgetColumn>
        )}
      </StyledContainer>
      <NonPrintingWrapper>
        <GhostTiles />
      </NonPrintingWrapper>
    </>
  )
}

export default Dashboard
